import { PaymentMethodEnum } from 'config/constants/payment'
import { Network, Token } from 'config/types'
import { ValidationError } from 'config/types/validator'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import Column from 'layout/Components/Column'
import { RowBetween } from 'layout/Components/Row'
import { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useAuth } from 'state/auth/hooks'
import { FlexProps } from 'UIKit/Box/types'
import ContractInfo from 'views/Payment/components/ContractInfo'
import { PaymentPageEnums } from 'views/Payment/Menu'
import SelectedBonusItem from '../../components/SelectedBonusItem'
import SelectedNetworkItem from '../../components/SelectedNetworkItem'
import {
  AmountDisplay,
  PaymentFormControl,
  PaymentFormInputAmount,
  PaymentLabel,
  PaymentStroke,
  StickyButtonWrapper,
} from '../../components/styles'

import ExtraInfo, { ExtraInfoItem } from '../../../components/ExtraInfo'
import { DepositModeEnums, DepositWalletControls, useDepositControl } from '../../hooks/useDeposit'
import { IBonusControl } from '../../types'
import DepositButton from './DepositButtons/DepositButton'
import SwitchAddressToDepositButton from './DepositButtons/SwitchAddressToDepositButton'
import SwitchNetworkToDepositButton from './DepositButtons/SwitchNetworkToDepositButton'

const DepositInputErrorMessages = {
  depositValue: {
    [ValidationError.Insufficient]: 'Insufficient balance',
  },
}

interface DepositByContractProps {
  selectedToken: Token
  selectedNetwork: Network
  isDepositing: boolean
  onChangeDepositing: (isDepositing: boolean) => void
  bonusControl: IBonusControl

  onSelectToken?: (currency: Token) => void
}

const DepositByContract: React.FC<DepositByContractProps & FlexProps> = ({
  selectedToken,
  onChangeDepositing,
  isDepositing,
  onSelectToken,
  bonusControl,
  selectedNetwork,
}) => {
  const { isSigned } = useAuth()
  const { t } = useTranslation()
  const { isMobile } = useMatchBreakpoints()

  const {
    depositControl: {
      handler: { handleApprove, handleDeposit, handleSwitchNetwork },
      status: {
        tokenApproveProcessStatus,
        isTokenDepositInSubmitting,
        isNetworkSwitching,
        addressApproved,
        evmChainApproved,
        tokenApproved,
      },
      data: { allowance, connectedAccount, returnedForm, estimatedFee, tokenAmount, balance },
    },
  } = useDepositControl<DepositWalletControls>({
    mode: DepositModeEnums.WALLET,
    onStart: () => {
      onChangeDepositing(true)
    },
    onFailed: () => {
      onChangeDepositing(false)
    },
    onSucceed: () => {
      onChangeDepositing(false)
    },
    selectedToken,
    selectedNetwork,
    selectedBonus: bonusControl.selectedBonus,
    isDepositing,
  })

  const { states, controls, isValid } = useMemo(() => returnedForm, [returnedForm])

  return (
    <>
      {isMobile && (
        <Column gap="var(--pm-space-label, 8px)">
          <PaymentLabel>
            <Trans>Network</Trans>
          </PaymentLabel>
          <SelectedNetworkItem
            selectedNetwork={selectedNetwork}
            selectedToken={selectedToken}
            onSelectToken={onSelectToken}
            from={PaymentPageEnums.Deposit}
          />
        </Column>
      )}

      <Column gap="var(--pm-space-label, 8px)">
        <PaymentFormControl
          state={states.amount}
          label={t('Amount')}
          formatErrorMessage={() => DepositInputErrorMessages.depositValue[states.amount.errors[0]]}
          disabled={isDepositing}
        >
          <PaymentFormInputAmount
            tabIndex={1}
            control={controls.amount}
            name="amount"
            disabled={isDepositing}
            value={states.amount.value}
            token={selectedToken}
            errors={!isDepositing && states.amount.errors}
            validators={controls.amount.validators}
            onErrorChanged={controls.amount.onErrorChanged}
            onValueChanged={controls.amount.onValueChanged}
            max={tokenAmount?.amount}
            getFee={selectedToken.isNative ? estimatedFee : null}
          />
        </PaymentFormControl>

        <RowBetween>
          <AmountDisplay title="Wallet Balance" amount={balance} currency={selectedToken} />
          <AmountDisplay title="No Fee" textAlign="right" />
        </RowBetween>
      </Column>

      {bonusControl.bonuses.length > 0 && (
        <>
          <PaymentStroke />
          <SelectedBonusItem
            inputAmount={states.amount.value}
            bonusControl={bonusControl}
            selectedToken={selectedToken}
          />
        </>
      )}

      {selectedNetwork && (
        <>
          <ExtraInfo gap="20px" px="0" hide={!selectedToken?.address || selectedToken?.isNative}>
            <PaymentStroke />

            <ExtraInfoItem title="Contract Information">
              <ContractInfo from={PaymentMethodEnum.Deposit} token={selectedToken} />
            </ExtraInfoItem>
          </ExtraInfo>

          <StickyButtonWrapper>
            {isSigned && !addressApproved && <SwitchAddressToDepositButton />}
            {isSigned && addressApproved && !evmChainApproved && (
              <SwitchNetworkToDepositButton
                isSwitchingNetwork={isNetworkSwitching}
                onSwitchNetwork={handleSwitchNetwork}
                selectedToken={selectedToken}
              />
            )}
            {isSigned && addressApproved && evmChainApproved && (
              <DepositButton
                account={connectedAccount}
                allowance={allowance}
                approveProcess={tokenApproveProcessStatus}
                handleApproval={handleApprove}
                handleDeposit={handleDeposit}
                disabled={!isValid || !states.amount.value || bonusControl.isSelectedBonusExpired}
                isApproved={tokenApproved}
                submiting={isTokenDepositInSubmitting}
              />
            )}
          </StickyButtonWrapper>
        </>
      )}
    </>
  )
}

export default DepositByContract
